import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type Id, toast } from 'react-toastify';
import type { DynamicFormSettings } from '../@types/form-data-types';
import type { TransformedSimpleRecordDto } from '../api/@types/enhanced-v4-api.types';
import { enhancedV4Api, useGetSimpleRecordQuery } from '../api/enhanced/enhanced-v4-api';
import { useConfigurationContext } from '../context/configuration.context';
import { useDiagramContext } from '../context/diagram.context';
import { updateToast } from '../helpers/toast-util';
import { useAppDispatch } from '../redux/hooks';
import { setDiagramDisabled } from '../redux/slices/diagram';

/**
 * Clones a record by copying all relevant fields.
 * Shallow-copies the fields object (top-level keys only) so mutating the clone's fields
 * doesn't affect the source record; nested objects/arrays are shared by reference.
 *
 * Hierarchies are intentionally not copied from the source record. Omitting them
 * lets the server assign the cloning user's hierarchy (where the form is configured
 * to do so), matching how every other record is created in this app, rather than
 * inheriting the source bowtie's hierarchy.
 */
export const cloneRecordFields = (sourceRecord: TransformedSimpleRecordDto, form?: DynamicFormSettings) => {
  // Create a shallow copy of fields, preserving the original type structure
  const clonedFields = sourceRecord.fields ? { ...sourceRecord.fields } : {};

  return {
    formId: sourceRecord.formId,
    status: form?.initialStep?.label ?? sourceRecord.status,
    fields: clonedFields,
  };
};

/**
 * Custom hook for cloning an existing Bowtie with all its related records.
 * Handles the cloning process, progress tracking, and UI feedback.
 *
 * @returns Object containing:
 * - handleClick: Function to trigger the cloning process
 * - disabled: Boolean indicating if the clone process is in progress
 *
 * @remarks
 * This hook manages the entire lifecycle of cloning a bowtie:
 * - Fetches the source risk record and all linked records
 * - Creates new risk record with all field values
 * - Creates new cause and consequence records
 * - For controls: global controls are linked (not cloned), non-global controls are cloned
 * - Maintains proper linking between all records
 * - Shows progress notifications
 * - Resets RTK Query cache to ensure fresh data
 * - Navigates to the cloned bowtie on success
 *
 * @example
 * ```typescript
 * const { handleClick, disabled } = useCloneBowtie();
 * ```
 */
const useCloneBowtie = () => {
  const [isCloning, setIsCloning] = useState(false);
  const toastIdRef = useRef<Id | undefined>(undefined);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { recordId } = useDiagramContext();

  const configuration = useConfigurationContext();
  const {
    main: {
      formId: riskFormId,
      causesReverseRecordLinkFieldId,
      consequencesReverseRecordLinkFieldId,
      mitigatingControlsRecordLinkFieldId,
      preventativeControlsRecordLinkFieldId,
      escalationFactorsReverseRecordLinkFieldId,
    },
    causes: {
      formId: causesFormId,
      controlsReverseRecordLinkFieldId: causesControlsRecordLinkFieldId,
      mainFormRecordLinkFieldId: causesMainFormRecordLinkFieldId,
    },
    consequences: {
      formId: consequencesFormId,
      controlsReverseRecordLinkFieldId: consequencesControlsRecordLinkFieldId,
      mainFormRecordLinkFieldId: consequencesMainFormRecordLinkFieldId,
    },
    controls: {
      formId: controlFormId,
      isGlobalFieldId,
      preventativeControls: { causesRecordLinkFieldId },
      mitigatingControls: { consequencesRecordLinkFieldId },
    },
    escalationFactors: escalationFactorsConfig,
    escalationFactorBarriers: escalationFactorBarriersConfig,
  } = configuration;

  // Fetch the source risk record
  const { data: sourceRiskRecord } = useGetSimpleRecordQuery(
    { id: Number(recordId), params: {} },
    { skip: !recordId || isCloning }
  );

  /**
   * Checks all configured forms for RECORDLINK fields that are missing required configuration
   * (targetModuleId, targetModuleFormId, or recordLinkFormat).
   * Returns true if any misconfigured RECORDLINK field is found.
   */
  const hasMisconfiguredRecordLinkFields = useCallback(() => {
    const forms: DynamicFormSettings[] = [
      configuration.main.form,
      configuration.causes.form,
      configuration.consequences.form,
      configuration.controls.form,
    ];
    if (configuration.escalationFactors?.form) {
      forms.push(configuration.escalationFactors.form);
    }
    if (configuration.escalationFactorBarriers?.form) {
      forms.push(configuration.escalationFactorBarriers.form);
    }

    return forms.some((form) =>
      form.fieldGroups.some((group) =>
        group.fields.some(
          (field) =>
            field.fieldType === 'RECORDLINK' &&
            !field.targetModuleId &&
            !field.targetModuleFormId &&
            !field.recordLinkFormat
        )
      )
    );
  }, [configuration]);

  const handleClick = useCallback(async () => {
    if (!sourceRiskRecord || !recordId) {
      toast.error('No bowtie selected to clone', { autoClose: 2000 });
      return;
    }

    if (hasMisconfiguredRecordLinkFields()) {
      toast.error(t('clone-bowtie-misconfigured-record-link-fields'), { autoClose: 5000 });
      return;
    }

    setIsCloning(true);
    dispatch(setDiagramDisabled(true));
    toastIdRef.current = toast.loading('Cloning bowtie...');

    try {
      // Step 1: Clone the risk record
      toast.update(toastIdRef.current, {
        render: 'Cloning risk record...',
        isLoading: true,
      });

      const newRiskData = cloneRecordFields(sourceRiskRecord, configuration.main.form);
      const riskResponse = await dispatch(
        enhancedV4Api.endpoints.createSimpleRecords.initiate({
          params: {},
          body: [newRiskData],
        })
      ).unwrap();

      if (riskResponse.length === 0 || !riskResponse[0].success || !riskResponse[0].id) {
        throw new Error('Failed to create risk record');
      }

      const newRiskId = riskResponse[0].id;

      // Step 2: Fetch and clone causes
      const causesRecordIds = (sourceRiskRecord.fields?.[causesReverseRecordLinkFieldId] as Array<number>) || [];

      const newCauseIds: number[] = [];
      const causeMapping = new Map<number, number>(); // old ID -> new ID
      let causesRecordsResponse: TransformedSimpleRecordDto[] = [];

      if (causesRecordIds.length > 0) {
        toast.update(toastIdRef.current, {
          render: `Cloning causes (0/${causesRecordIds.length})...`,
          isLoading: true,
        });

        causesRecordsResponse = await dispatch(
          enhancedV4Api.endpoints.getSimpleRecords1.initiate({
            formId: causesFormId,
            filter: `id:in:${causesRecordIds.join(',')}`,
            pageSize: 50,
          })
        ).unwrap();

        for (let i = 0; i < causesRecordsResponse.length; i++) {
          const sourceCause = causesRecordsResponse[i];
          toast.update(toastIdRef.current, {
            render: `Cloning causes (${i + 1}/${causesRecordsResponse.length})...`,
            isLoading: true,
          });

          const newCauseData = cloneRecordFields(sourceCause, configuration.causes.form);
          // Link to new risk
          newCauseData.fields![causesMainFormRecordLinkFieldId!] = [newRiskId];

          const causeResponse = await dispatch(
            enhancedV4Api.endpoints.createSimpleRecords.initiate({
              params: {},
              body: [newCauseData],
            })
          ).unwrap();

          if (causeResponse.length > 0 && causeResponse[0].success && causeResponse[0].id) {
            const newCauseId = causeResponse[0].id;
            newCauseIds.push(newCauseId);
            causeMapping.set(sourceCause.id!, newCauseId);
          }
        }

        // Update risk record with new cause IDs
        await dispatch(
          enhancedV4Api.endpoints.patchSimple.initiate({
            id: newRiskId,
            params: {},
            simpleRecordDto: {
              formId: riskFormId,
              fields: {
                [causesReverseRecordLinkFieldId]: newCauseIds,
              },
            },
          })
        ).unwrap();
      }

      // Step 3: Fetch and clone consequences
      const consequencesRecordIds =
        (sourceRiskRecord.fields?.[consequencesReverseRecordLinkFieldId] as Array<number>) || [];

      const newConsequenceIds: number[] = [];
      const consequenceMapping = new Map<number, number>(); // old ID -> new ID
      let consequencesRecordsResponse: TransformedSimpleRecordDto[] = [];

      if (consequencesRecordIds.length > 0) {
        toast.update(toastIdRef.current, {
          render: `Cloning consequences (0/${consequencesRecordIds.length})...`,
          isLoading: true,
        });

        consequencesRecordsResponse = await dispatch(
          enhancedV4Api.endpoints.getSimpleRecords1.initiate({
            formId: consequencesFormId,
            filter: `id:in:${consequencesRecordIds.join(',')}`,
            pageSize: 50,
          })
        ).unwrap();

        for (let i = 0; i < consequencesRecordsResponse.length; i++) {
          const sourceConsequence = consequencesRecordsResponse[i];
          toast.update(toastIdRef.current, {
            render: `Cloning consequences (${i + 1}/${consequencesRecordsResponse.length})...`,
            isLoading: true,
          });

          const newConsequenceData = cloneRecordFields(sourceConsequence, configuration.consequences.form);
          // Link to new risk
          newConsequenceData.fields![consequencesMainFormRecordLinkFieldId!] = [newRiskId];

          const consequenceResponse = await dispatch(
            enhancedV4Api.endpoints.createSimpleRecords.initiate({
              params: {},
              body: [newConsequenceData],
            })
          ).unwrap();

          if (consequenceResponse.length > 0 && consequenceResponse[0].success && consequenceResponse[0].id) {
            const newConsequenceId = consequenceResponse[0].id;
            newConsequenceIds.push(newConsequenceId);
            consequenceMapping.set(sourceConsequence.id!, newConsequenceId);
          }
        }

        // Update risk record with new consequence IDs
        await dispatch(
          enhancedV4Api.endpoints.patchSimple.initiate({
            id: newRiskId,
            params: {},
            simpleRecordDto: {
              formId: riskFormId,
              fields: {
                [consequencesReverseRecordLinkFieldId]: newConsequenceIds,
              },
            },
          })
        ).unwrap();
      }

      // Step 4: Fetch and clone controls
      const preventativeControlsRecordIds =
        (sourceRiskRecord.fields?.[preventativeControlsRecordLinkFieldId] as Array<string>) || [];
      const mitigatingControlsRecordIds =
        (sourceRiskRecord.fields?.[mitigatingControlsRecordLinkFieldId] as Array<string>) || [];

      const allControlIds = [...preventativeControlsRecordIds, ...mitigatingControlsRecordIds];

      const newPreventativeControlIds: number[] = [];
      const newMitigatingControlIds: number[] = [];
      const controlMapping = new Map<number, number>(); // old control ID -> new control ID

      if (allControlIds.length > 0) {
        let controlRecordsResponse: TransformedSimpleRecordDto[] = [];

        toast.update(toastIdRef.current, {
          render: `Cloning controls (0/${allControlIds.length})...`,
          isLoading: true,
        });

        controlRecordsResponse = await dispatch(
          enhancedV4Api.endpoints.getSimpleRecords1.initiate({
            formId: controlFormId,
            filter: `id:in:${allControlIds.join(',')}`,
            pageSize: 200,
          })
        ).unwrap();

        for (let i = 0; i < controlRecordsResponse.length; i++) {
          const sourceControl = controlRecordsResponse[i];
          toast.update(toastIdRef.current, {
            render: `Cloning controls (${i + 1}/${controlRecordsResponse.length})...`,
            isLoading: true,
          });

          // Determine if this is a preventative or mitigating control
          const isPreventative = preventativeControlsRecordIds.includes(String(sourceControl.id));

          // Check if this is a global control
          const isGlobal = sourceControl.fields?.[isGlobalFieldId] === true;

          let newControlId: number;

          if (isGlobal) {
            // For global controls, reuse the existing control ID instead of cloning
            newControlId = sourceControl.id!;

            // Store the mapping (old ID -> same ID for global controls)
            controlMapping.set(sourceControl.id!, newControlId);

            // Note: We don't need to patch the global control here.
            // The bidirectional linking will be handled later when we update
            // the causes/consequences to link to this global control.
            // This avoids modifying the global control's links to other bowties.
          } else {
            // For non-global controls, clone as usual
            const newControlData = cloneRecordFields(sourceControl, configuration.controls.form);

            // Update linked causes or consequences to point to new records
            if (isPreventative && causesRecordLinkFieldId) {
              const oldCauseIds = (sourceControl.fields![causesRecordLinkFieldId] as Array<string>) || [];
              const newCauseIds = oldCauseIds
                .map((oldId) => causeMapping.get(Number(oldId)))
                .filter((id): id is number => id !== undefined);
              newControlData.fields![causesRecordLinkFieldId] = newCauseIds.map(String);
            } else if (!isPreventative && consequencesRecordLinkFieldId) {
              const oldConsequenceIds = (sourceControl.fields![consequencesRecordLinkFieldId] as Array<string>) || [];
              const newConsequenceIds = oldConsequenceIds
                .map((oldId) => consequenceMapping.get(Number(oldId)))
                .filter((id): id is number => id !== undefined);
              newControlData.fields![consequencesRecordLinkFieldId] = newConsequenceIds.map(String);
            }

            const controlResponse = await dispatch(
              enhancedV4Api.endpoints.createSimpleRecords.initiate({
                params: {},
                body: [newControlData],
              })
            ).unwrap();

            if (controlResponse.length > 0 && controlResponse[0].success && controlResponse[0].id) {
              newControlId = controlResponse[0].id;

              // Store the mapping from old control ID to new control ID
              controlMapping.set(sourceControl.id!, newControlId);
            } else {
              // If control creation failed, skip this control
              continue;
            }
          }

          // Add the control ID to the appropriate list
          if (isPreventative) {
            newPreventativeControlIds.push(newControlId);
          } else {
            newMitigatingControlIds.push(newControlId);
          }
        }

        // Update risk record with new control IDs
        await dispatch(
          enhancedV4Api.endpoints.patchSimple.initiate({
            id: newRiskId,
            params: {},
            simpleRecordDto: {
              formId: riskFormId,
              fields: {
                [preventativeControlsRecordLinkFieldId]: newPreventativeControlIds.map(String),
                [mitigatingControlsRecordLinkFieldId]: newMitigatingControlIds.map(String),
              },
            },
          })
        ).unwrap();

        // Track which global controls need to be updated with new links
        const globalControlUpdates = new Map<
          number,
          {
            causesToAdd: number[];
            consequencesToAdd: number[];
          }
        >();

        // Update causes with their linked controls
        for (const [oldCauseId, newCauseId] of causeMapping.entries()) {
          const sourceCause = causesRecordsResponse.find((cause) => cause.id === oldCauseId);
          if (sourceCause) {
            const oldControlIds = (sourceCause.fields![causesControlsRecordLinkFieldId] as Array<number>) || [];
            // Map old control IDs to new control IDs using the controlMapping
            const newControlIds = oldControlIds
              .map((oldControlId) => controlMapping.get(oldControlId))
              .filter((id): id is number => id !== undefined);

            if (newControlIds.length > 0) {
              await dispatch(
                enhancedV4Api.endpoints.patchSimple.initiate({
                  id: newCauseId,
                  params: {},
                  simpleRecordDto: {
                    formId: causesFormId,
                    fields: {
                      [causesControlsRecordLinkFieldId]: newControlIds,
                    },
                  },
                })
              ).unwrap();

              // Track global controls that need updating
              for (const controlId of newControlIds) {
                const sourceControl = controlRecordsResponse.find((c) => c.id === controlId);
                if (sourceControl?.fields?.[isGlobalFieldId] === true) {
                  if (!globalControlUpdates.has(controlId)) {
                    globalControlUpdates.set(controlId, { causesToAdd: [], consequencesToAdd: [] });
                  }
                  globalControlUpdates.get(controlId)!.causesToAdd.push(newCauseId);
                }
              }
            }
          }
        }

        // Update consequences with their linked controls
        for (const [oldConsequenceId, newConsequenceId] of consequenceMapping.entries()) {
          const sourceConsequence = consequencesRecordsResponse.find(
            (consequence) => consequence.id === oldConsequenceId
          );
          if (sourceConsequence) {
            const oldControlIds =
              (sourceConsequence.fields![consequencesControlsRecordLinkFieldId] as Array<number>) || [];
            // Map old control IDs to new control IDs using the controlMapping
            const newControlIds = oldControlIds
              .map((oldControlId) => controlMapping.get(oldControlId))
              .filter((id): id is number => id !== undefined);

            if (newControlIds.length > 0) {
              await dispatch(
                enhancedV4Api.endpoints.patchSimple.initiate({
                  id: newConsequenceId,
                  params: {},
                  simpleRecordDto: {
                    formId: consequencesFormId,
                    fields: {
                      [consequencesControlsRecordLinkFieldId]: newControlIds,
                    },
                  },
                })
              ).unwrap();

              // Track global controls that need updating
              for (const controlId of newControlIds) {
                const sourceControl = controlRecordsResponse.find((c) => c.id === controlId);
                if (sourceControl?.fields?.[isGlobalFieldId] === true) {
                  if (!globalControlUpdates.has(controlId)) {
                    globalControlUpdates.set(controlId, { causesToAdd: [], consequencesToAdd: [] });
                  }
                  globalControlUpdates.get(controlId)!.consequencesToAdd.push(newConsequenceId);
                }
              }
            }
          }
        }

        // Update global controls with their new cause/consequence links
        for (const [globalControlId, updates] of globalControlUpdates.entries()) {
          const sourceControl = controlRecordsResponse.find((c) => c.id === globalControlId);
          if (!sourceControl) continue;

          const fieldsToUpdate: Record<string, Array<string>> = {};

          if (updates.causesToAdd.length > 0 && causesRecordLinkFieldId) {
            const existingCauseIds = (sourceControl.fields![causesRecordLinkFieldId] as Array<string>) || [];
            const allCauseIds = [...new Set([...existingCauseIds, ...updates.causesToAdd.map(String)])];
            fieldsToUpdate[causesRecordLinkFieldId] = allCauseIds;
          }

          if (updates.consequencesToAdd.length > 0 && consequencesRecordLinkFieldId) {
            const existingConsequenceIds =
              (sourceControl.fields![consequencesRecordLinkFieldId] as Array<string>) || [];
            const allConsequenceIds = [
              ...new Set([...existingConsequenceIds, ...updates.consequencesToAdd.map(String)]),
            ];
            fieldsToUpdate[consequencesRecordLinkFieldId] = allConsequenceIds;
          }

          if (Object.keys(fieldsToUpdate).length > 0) {
            await dispatch(
              enhancedV4Api.endpoints.patchSimple.initiate({
                id: globalControlId,
                params: {},
                simpleRecordDto: {
                  formId: controlFormId,
                  fields: fieldsToUpdate,
                },
              })
            ).unwrap();
          }
        }
      }

      // Step 5: Clone Escalation Factors (EFs)
      // EFs must be cloned before EFBs because EFBs link to EFs
      if (escalationFactorsConfig && escalationFactorBarriersConfig && escalationFactorsReverseRecordLinkFieldId) {
        const {
          formId: efFormId,
          mainFormRecordLinkFieldId: efMainFormRecordLinkFieldId,
          controlRecordLinkFieldId: efControlRecordLinkFieldId,
          barriersReverseRecordLinkFieldId: efBarriersReverseRecordLinkFieldId,
        } = escalationFactorsConfig;

        const {
          formId: efbFormId,
          controlRecordLinkFieldId: efbControlRecordLinkFieldId,
          escalationFactorRecordLinkFieldId: efbEscalationFactorRecordLinkFieldId,
          mainFormRecordLinkFieldId: efbMainFormRecordLinkFieldId,
        } = escalationFactorBarriersConfig;

        const escalationFactorsRecordIds =
          (sourceRiskRecord.fields?.[escalationFactorsReverseRecordLinkFieldId] as Array<number>) || [];

        const efMapping = new Map<number, number>(); // old EF ID -> new EF ID
        const newEfIds: number[] = [];

        if (escalationFactorsRecordIds.length > 0) {
          toast.update(toastIdRef.current, {
            render: `Cloning escalation factors (0/${escalationFactorsRecordIds.length})...`,
            isLoading: true,
          });

          const efRecordsResponse = await dispatch(
            enhancedV4Api.endpoints.getSimpleRecords1.initiate({
              formId: efFormId,
              filter: `id:in:${escalationFactorsRecordIds.join(',')}`,
              pageSize: 100,
            })
          ).unwrap();

          for (let i = 0; i < efRecordsResponse.length; i++) {
            const sourceEf = efRecordsResponse[i];
            toast.update(toastIdRef.current, {
              render: `Cloning escalation factors (${i + 1}/${efRecordsResponse.length})...`,
              isLoading: true,
            });

            const newEfData = cloneRecordFields(sourceEf, escalationFactorsConfig?.form);

            // Link to new risk
            newEfData.fields![efMainFormRecordLinkFieldId] = [newRiskId];

            // Update control link to point to the new cloned control (NOT old control!)
            // Note: Control links might need to be strings, so we handle both cases
            const oldControlId = (sourceEf.fields![efControlRecordLinkFieldId] as Array<number | string>)?.[0];
            if (oldControlId) {
              const oldControlIdNum = typeof oldControlId === 'string' ? Number(oldControlId) : oldControlId;
              const newControlId = controlMapping.get(oldControlIdNum);
              if (newControlId) {
                // Check if the original field used strings or numbers
                const useString = typeof oldControlId === 'string';
                newEfData.fields![efControlRecordLinkFieldId] = useString ? [String(newControlId)] : [newControlId];
              } else {
                // If no new control found, remove the old control link
                delete newEfData.fields![efControlRecordLinkFieldId];
              }
            }

            // IMPORTANT: Remove old barrier IDs - we'll add new ones after EFBs are cloned
            delete newEfData.fields![efBarriersReverseRecordLinkFieldId];

            const efResponse = await dispatch(
              enhancedV4Api.endpoints.createSimpleRecords.initiate({
                params: {},
                body: [newEfData],
              })
            ).unwrap();

            if (efResponse.length > 0 && efResponse[0].success && efResponse[0].id) {
              const newEfId = efResponse[0].id;
              newEfIds.push(newEfId);
              efMapping.set(sourceEf.id!, newEfId);
            }
          }
        }

        // Step 6: Clone Escalation Factor Barriers (EFBs)
        // Now clone EFBs and link them to the new EFs
        const efbMapping = new Map<number, number>(); // old EFB ID -> new EFB ID

        // Collect all EFB IDs from the cloned EF records
        const allEfbIds: number[] = [];

        if (escalationFactorsRecordIds.length > 0) {
          const efRecordsResponse = await dispatch(
            enhancedV4Api.endpoints.getSimpleRecords1.initiate({
              formId: efFormId,
              filter: `id:in:${escalationFactorsRecordIds.join(',')}`,
              pageSize: 100,
            })
          ).unwrap();

          // Collect all EFB IDs from the source EF records
          efRecordsResponse.forEach((sourceEf) => {
            if (sourceEf.fields?.[efBarriersReverseRecordLinkFieldId]) {
              const efbIds = sourceEf.fields[efBarriersReverseRecordLinkFieldId] as Array<number>;
              if (Array.isArray(efbIds)) {
                allEfbIds.push(...efbIds);
              }
            }
          });
        }

        const uniqueEfbIds = [...new Set(allEfbIds)];

        if (uniqueEfbIds.length > 0) {
          toast.update(toastIdRef.current, {
            render: `Cloning escalation factor barriers (0/${uniqueEfbIds.length})...`,
            isLoading: true,
          });

          const efbRecordsResponse = await dispatch(
            enhancedV4Api.endpoints.getSimpleRecords1.initiate({
              formId: efbFormId,
              filter: `id:in:${uniqueEfbIds.join(',')}`,
              pageSize: 100,
            })
          ).unwrap();

          for (let i = 0; i < efbRecordsResponse.length; i++) {
            const sourceEfb = efbRecordsResponse[i];
            toast.update(toastIdRef.current, {
              render: `Cloning escalation factor barriers (${i + 1}/${efbRecordsResponse.length})...`,
              isLoading: true,
            });

            const newEfbData = cloneRecordFields(sourceEfb, escalationFactorBarriersConfig?.form);

            // Link to new CRA (risk record)
            newEfbData.fields![efbMainFormRecordLinkFieldId] = [newRiskId];

            // Update control link to point to the NEW cloned control (NOT old control!)
            // Note: Control links might need to be strings, so we handle both cases
            const oldControlId = (sourceEfb.fields![efbControlRecordLinkFieldId] as Array<number | string>)?.[0];
            if (oldControlId) {
              const oldControlIdNum = typeof oldControlId === 'string' ? Number(oldControlId) : oldControlId;
              const newControlId = controlMapping.get(oldControlIdNum);
              if (newControlId) {
                // Check if the original field used strings or numbers
                const useString = typeof oldControlId === 'string';
                newEfbData.fields![efbControlRecordLinkFieldId] = useString ? [String(newControlId)] : [newControlId];
              } else {
                // If no new control found, remove the old control link
                delete newEfbData.fields![efbControlRecordLinkFieldId];
              }
            }

            // Update EF link to point to the NEW cloned EF (NOT old EF!)
            const oldEfId = (sourceEfb.fields![efbEscalationFactorRecordLinkFieldId] as Array<number | string>)?.[0];
            if (oldEfId) {
              const oldEfIdNum = typeof oldEfId === 'string' ? Number(oldEfId) : oldEfId;
              const newEfId = efMapping.get(oldEfIdNum);
              if (newEfId) {
                // Check if the original field used strings or numbers
                const useString = typeof oldEfId === 'string';
                newEfbData.fields![efbEscalationFactorRecordLinkFieldId] = useString ? [String(newEfId)] : [newEfId];
              } else {
                // If no new EF found, remove the old EF link
                delete newEfbData.fields![efbEscalationFactorRecordLinkFieldId];
              }
            }

            const efbResponse = await dispatch(
              enhancedV4Api.endpoints.createSimpleRecords.initiate({
                params: {},
                body: [newEfbData],
              })
            ).unwrap();

            if (efbResponse.length > 0 && efbResponse[0].success && efbResponse[0].id) {
              efbMapping.set(sourceEfb.id!, efbResponse[0].id);
            }
          }
        }

        // Update all cloned EFs with their new EFB IDs
        if (newEfIds.length > 0) {
          const efRecordsResponse = await dispatch(
            enhancedV4Api.endpoints.getSimpleRecords1.initiate({
              formId: efFormId,
              filter: `id:in:${escalationFactorsRecordIds.join(',')}`,
              pageSize: 100,
            })
          ).unwrap();

          for (const sourceEf of efRecordsResponse) {
            const newEfId = efMapping.get(sourceEf.id!);
            if (!newEfId) continue;

            // Handle both string and number types for barrier IDs
            const oldBarrierIds =
              (sourceEf.fields![efBarriersReverseRecordLinkFieldId] as Array<number | string>) || [];
            const useString = oldBarrierIds.length > 0 && typeof oldBarrierIds[0] === 'string';
            const newBarrierIds = oldBarrierIds
              .map((oldBarrierId) => {
                const oldBarrierIdNum = typeof oldBarrierId === 'string' ? Number(oldBarrierId) : oldBarrierId;
                return efbMapping.get(oldBarrierIdNum);
              })
              .filter((id): id is number => id !== undefined);

            if (newBarrierIds.length > 0) {
              const barrierIdsToSet = useString ? newBarrierIds.map(String) : newBarrierIds;

              await dispatch(
                enhancedV4Api.endpoints.patchSimple.initiate({
                  id: newEfId,
                  params: {},
                  simpleRecordDto: {
                    formId: efFormId,
                    fields: {
                      [efBarriersReverseRecordLinkFieldId]: barrierIdsToSet,
                    },
                  },
                })
              ).unwrap();
            }
          }
        }

        // Update risk record with new EF IDs
        if (newEfIds.length > 0) {
          await dispatch(
            enhancedV4Api.endpoints.patchSimple.initiate({
              id: newRiskId,
              params: {},
              simpleRecordDto: {
                formId: riskFormId,
                fields: {
                  [escalationFactorsReverseRecordLinkFieldId]: newEfIds,
                },
              },
            })
          ).unwrap();
        }
      }

      // Success! Navigate to the cloned bowtie
      updateToast(toastIdRef.current, 'Bowtie cloned successfully!', 'success');

      // Invalidate all cached queries to ensure fresh data is fetched
      dispatch(enhancedV4Api.util.resetApiState());

      // Navigate to the cloned bowtie using React Router
      navigate({ search: `records=${newRiskId}` }, { replace: true });
    } catch (error) {
      console.error('Clone error:', error);
      updateToast(toastIdRef.current, 'Failed to clone bowtie. Please try again.', 'error');
    } finally {
      setIsCloning(false);
      dispatch(setDiagramDisabled(false));
    }
  }, [
    sourceRiskRecord,
    recordId,
    hasMisconfiguredRecordLinkFields,
    dispatch,
    navigate,
    riskFormId,
    causesFormId,
    consequencesFormId,
    controlFormId,
    causesReverseRecordLinkFieldId,
    consequencesReverseRecordLinkFieldId,
    mitigatingControlsRecordLinkFieldId,
    preventativeControlsRecordLinkFieldId,
    causesControlsRecordLinkFieldId,
    causesMainFormRecordLinkFieldId,
    consequencesControlsRecordLinkFieldId,
    consequencesMainFormRecordLinkFieldId,
    causesRecordLinkFieldId,
    consequencesRecordLinkFieldId,
    isGlobalFieldId,
    escalationFactorsConfig,
    escalationFactorBarriersConfig,
    escalationFactorsReverseRecordLinkFieldId,
  ]);

  return {
    handleClick,
    disabled: isCloning,
  };
};

export default useCloneBowtie;
